import moment from "moment";
import ProductSelectionComponent from "../../../components/model-management/product-selection.component";
import SelectModelAliasSearchInputComponent from "../../../components/model/select-model-alias-search-input.component";
import MultiSelectComponent from "../../../components/multi-select/multi-select.component";
import paginationComponent from "../../../components/pagination/pagination.component";
import DynamicDateComponent from "../../../components/report/dynamic-date.component";
import { DATE_TYPE_CODES, formats } from "../../../config/constants";
import ShopOwnerReportModel from "../../../models/reports/shop-owner/shop-owner-report.model";
import ViewBase from "../../view-base.view";
import loginModel from "../../../models/login.model";

/**
* Shop Owner Report
* 
* @author cmc.nttruong1
*/

export default function ShopOwnerReportView() {
  ViewBase.call(this);
  const SUMMARY_TABLE_ID = "summary-id";
  const REPORT_TABLE_ID = "report-id";
  const STATIC_COLUMNS = { SITE_ID: 'SiteId', SITE_NAME: 'SiteName', TOTAL: 'Total' };
  const SUPPORTED_DATES = ['D', 'W', 'M'];

  const DEFAULT_FILTERS = {
    periodCode: DATE_TYPE_CODES.DATE,
    periodStart: moment().format(formats.dateFormat),
    periodEnd: moment().format(formats.dateFormat),
    sites: [],
    model: [],
    product: [],
  }

  const DEFAULT_PAGINATION = {
    pageSize: 10,
    pageIndex: 1,
    totalItems: 0
  }

  const DATE_TYPE_LIMITS = {
    [DATE_TYPE_CODES.DATE]: 31,
    [DATE_TYPE_CODES.WEEK]: 10,
    [DATE_TYPE_CODES.MONTH]: 5
  };

  let filters = structuredClone(DEFAULT_FILTERS);
  let pagination = structuredClone(DEFAULT_PAGINATION);

  let listSummaries = [];
  let listReports = [];
  let listSiteOptions = [];
  let listDynamicColumnHeaders = [];
  let currentFilters = {};
  let apiFormatDate = '';

  let currency = '';
  let country = '';

  const getListSites = () => {
    ShopOwnerReportModel.getListSites()
      .then((result) => {
        if (result) {
          listSiteOptions = result.map((item) => {
            return {
              code: item.siteCode,
              name: item.siteName,
            };
          });
        }
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  const toParams = (filters, pagination) => {
    return { ...filters, pageIndex: pagination.pageIndex, pageSize: pagination.pageSize };
  }

  const handleSearch = (e) => {
    e.preventDefault();

    const collapsedElements = document.querySelectorAll('.collapsed');
    collapsedElements.forEach((e) => e.click());

    if (e.target.checkValidity()) {
      pagination.pageIndex = DEFAULT_PAGINATION.pageIndex;

      switch (filters.periodCode) {
        case DATE_TYPE_CODES.MONTH:
          apiFormatDate = formats.API_MONTH_FORMAT;
          break;
        case DATE_TYPE_CODES.WEEK:
          apiFormatDate = formats.API_WEEK_FORMAT;
          break;
        default:
          apiFormatDate = formats.apiDateFormat;
          break;
      }

      let modelAliases = filters.model.map((item) => { return item.modelAlias });
      let payload = Object.assign({
        fromDate: moment(filters.periodStart).format(apiFormatDate),
        toDate: moment(filters.periodEnd).format(apiFormatDate),
        dateType: filters.periodCode,
        siteIds: filters.sites.join('^'),
        modelAliases: modelAliases.join('^'),
        product: filters.product.join('^'),
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      });
      search(payload);
    }

    e.target.classList.add("was-validated");
  }

  const search = (payload) => {
    this.showLoading();

    ShopOwnerReportModel.searchShopOwnerReport(payload)
      .then((result) => {
        currentFilters = structuredClone(payload);
        listDynamicColumnHeaders = [];

        // invalid result 
        if (!result) {
          listSummaries = [];
          listReports = [];
          return;
        }

        listReports = result.report;
        if (listReports && listReports.length > 0) {
          currency = listReports[0].currency || ''
        }
        if (result.pageIndex === 1) {
          listSummaries = result.summary;
          pagination.totalItems = result.total;
        }

        // Define dynamic headers based on list results regardless of page index
        if (listSummaries.length > 0) {
          listDynamicColumnHeaders = Object.keys(listSummaries[0]).filter((key) => !Object.values(STATIC_COLUMNS).includes(key));
        }
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => { this.hideLoading(); })
  }

  const clearFilters = () => {
    filters = structuredClone(DEFAULT_FILTERS);
  }

  const download = (payload) => {
    this.showLoading();
    ShopOwnerReportModel.downloadExcel(payload)
      .catch((error) => { this.handleError(error) })
      .finally(() => { this.hideLoading() })
  }

  const handleClickDownload = (e) => {
    e.preventDefault();
    download(currentFilters);
  }

  const onPageSizeChanged = (pageIndex, pageSize) => {
    pagination.pageIndex = pageIndex;
    pagination.pageSize = pageSize;
    search(toParams(currentFilters, pagination));
  };

  const onPageChanged = (pageIndex) => {
    pagination.pageIndex = pageIndex;
    search(toParams(currentFilters, pagination));
  };

  return {
    oninit: () => {
      getListSites();
      country = loginModel.credentials.country;
    },
    view: () => {
      return [
        /* SEARCH */
        m(".d-flex.flex-column.align-items-end.gap-2.mb-3",
          m('a.btn.btn-primary[data-bs-toggle="collapse"][href="#search-section"][aria-controls="search-section"][aria-expanded="false"]',
            m("i.bi.bi-filter"),
            this.getText("LT00000232", "Filter")
          ),
          m(".collapse.show#search-section.w-100",
            m('.card.mb-3',
              m('.card-body',
                m('form.needs-validation[novalidate].row.g-2.align-items-end',
                  { onsubmit: handleSearch },
                  m('.col-xl-3.col-lg-4.col-sm-6',
                    m(DynamicDateComponent, {
                      selectedDateType: filters.periodCode,
                      supportedDates: SUPPORTED_DATES,
                      fromDate: filters.periodStart,
                      toDate: filters.periodEnd,
                      required: true,
                      max: DATE_TYPE_LIMITS[filters.periodCode],
                      onChange: (dateType, fromDate, toDate) => {
                        filters.periodCode = dateType;
                        filters.periodStart = fromDate;
                        filters.periodEnd = toDate;
                      },
                    })
                  ),
                  m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                    m('label.form-label[for="sites"]', this.getText('LT00008076', 'Sites')),
                    m(MultiSelectComponent, {
                      id: 'sites',
                      items: listSiteOptions,
                      selectedItems: filters.sites,
                      keyOnly: true,
                      filterableByKey: true,
                      showKeyInLabel: true,
                      onSelect: (value) => {
                        filters.sites = value;
                      }
                    }),
                  ),
                  m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                    m('label.form-label[for="model"]', this.getText('LT00000717', 'Model')),
                    m(SelectModelAliasSearchInputComponent, {
                      id: 'model',
                      multiple: true,
                      selectedItems: filters.model,
                      onSelect: (value) => {
                        filters.model = value;
                      }
                    }),
                  ),
                  m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                    m('label.form-label[for="product"]', this.getText('LT00000579', 'Product')),
                    m(ProductSelectionComponent, {
                      id: 'product',
                      selectedItems: filters.product,
                      onChange: (value) => {
                        filters.product = value;
                      }
                    }),
                  ),

                  // Search & clear buttons
                  m('.col-12.d-flex.justify-content-end.gap-2',
                    m('button.btn.btn-primary', this.getText("LT00000001", "Search")),
                    m('button.btn.btn-white[type="button"]',
                      { onclick: clearFilters },
                      this.getText("LT00000031", "Clear")),
                  ),
                )
              )
            ),
          ),
        ),

        /* TABLE */
        m(".card",
          m(".card-body",
            m('.row.g-2.mb-2',
              m('.col-12.d-flex.justify-content-end',
                m('button.btn.btn-primary', {
                  onclick: handleClickDownload,
                  disabled: !(listSummaries.length > 0 || listReports.length > 0),
                },
                  m('i.bi.bi-file-earmark-arrow-down-fill'),
                  this.getText('LT00000018', 'Excel Download'))
              ),
            ),
            m(`.accordion#shop-owner-report-accordion`,

              // FIRST TABLE
              m(".accordion-item.rounded-0.border-top-0.border-start-0.border-end-0",
                m(`h2.accordion-header#${SUMMARY_TABLE_ID}-heading`,
                  m(`button.rounded-0.accordion-button.collapsed[aria-expanded=false][type=button][data-bs-toggle=collapse][data-bs-target=#${SUMMARY_TABLE_ID}-collapse][aria-controls=${SUMMARY_TABLE_ID}-collapse]`,
                    m("span", this.getText("LT00001095", "Summary")),
                    m("span.ms-5")
                  )
                ),
                m(`.accordion-collapse.collapse#${SUMMARY_TABLE_ID}-collapse[aria-labelledby=${SUMMARY_TABLE_ID}-heading]#${SUMMARY_TABLE_ID}-collapse`,
                  m(".accordion-body.px-0",
                    m(".row.g-2.mb-3",
                      m(".col-12",
                        m(".table-responsive",
                          m("table.table.table-hover.sticky-column-start",
                            m("thead",
                              m("tr.align-middle",
                                m("th", this.getText("LT00008070", "Site Id")),
                                m("th", this.getText("LT00000144", "Site Name")),
                                listDynamicColumnHeaders.map((key) => {
                                  return m("th", key);
                                }),
                                m("th", this.getText("LT00000046", "Total"))
                              )
                            ),
                            m("tbody",
                              listSummaries.length == 0 &&
                              m("tr.align-middle",
                                m("td",
                                  { colspan: listDynamicColumnHeaders.length + 3 },
                                  this.getText(
                                    "EM00000141",
                                    "No records found."
                                  )
                                )
                              ),
                              listSummaries.map((row) =>
                                m(`tr.align-middle.cursor-pointer`,
                                  m("td", m("span.cursor-text", row[STATIC_COLUMNS.SITE_ID])),
                                  m("td", m("span.cursor-text", { style: "display: inline-block; border: none; max-width: 500px; min-width: 300px; width: auto; white-space: initial;" }, row[STATIC_COLUMNS.SITE_NAME])),
                                  listDynamicColumnHeaders.map((key) => {
                                    return m("td", m("span.cursor-text", row[key]));
                                  }),
                                  m("td", m("span.cursor-text", row[STATIC_COLUMNS.TOTAL])),
                                )
                              )
                            )
                          )
                        )
                      ),
                    ),

                  )
                )
              ),

              // SECOND TABLE
              m(".accordion-item.rounded-0.border-0",
                m(`h2.accordion-header#${REPORT_TABLE_ID}-heading`,
                  m(`button.rounded-0.accordion-button.collapsed[aria-expanded=false][type=button][data-bs-toggle=collapse][data-bs-target=#${REPORT_TABLE_ID}-collapse][aria-controls=${REPORT_TABLE_ID}-collapse]`,
                    m("span", this.getText("LT00008071", "Report")),
                    m("span.ms-5")
                  )
                ),
                m(`.accordion-collapse.collapse#${REPORT_TABLE_ID}-collapse[aria-labelledby=${REPORT_TABLE_ID}-heading]#${REPORT_TABLE_ID}-collapse`,
                  m(".accordion-body.px-0",
                    m(".row.g-2",
                      m(".col-12",
                        m(".table-responsive",
                          m("table.table.table-hover.table-bordered",
                            m("thead",
                              m("tr.align-middle",
                                m("th", this.getText("LT00008070", "Site Id")),
                                m("th", this.getText("LT00000144", "Site Name")),
                                country && country === 'MY' && m("th", this.getText("LT00000722", "Region")),
                                country && country === 'MY' && m("th", this.getText("LT00008143", "State")),
                                country && country === 'MY' && m("th", this.getText("LT00000714", "BU Channel Type")),
                                m("th", this.getText("LT00008072", "Survey YMD")),
                                m("th", this.getText("LT00008073", "Upload By")),
                                country && country === 'MY' && m("th", this.getText("LT00008144", "Channel Sales Executive (CSE)")),
                                country && country === 'MY' && m("th", this.getText("LT00008145", "Area General Manager (AGM)")),
                                country && country === 'MY' && m("th", this.getText("LT00008146", "Regional General Manager (RGM)")),
                                m("th", this.getText("LT00000898", "Model Alias")),
                                m("th", this.getText("LT00000748", "Model Code")),
                                country && country === 'MY' && m("th", this.getText("LT00008147", "Product Marketing Name")),
                                m("th", this.getText("LT00000908", "IMEI")),
                                m("th", this.getText("LT8069", "RRP") +(currency ? ` (${currency})` : '')),
                                m("th", this.getText("LT00008074", "Consumer Name")),
                                m("th", this.getText("LT00008075", "Consumer Mobile")),
                              )
                            ),
                            m("tbody",
                              listReports.length == 0 &&
                              m("tr.align-middle",
                                m("td",
                                  { colspan: "9" },
                                  this.getText("EM00000141", "No records found.")
                                )
                              ),
                              listReports.map((row, index) =>
                                m(`tr.align-middle.cursor-pointer`,
                                  m("td", m("span.cursor-text", row.siteId)),
                                  m("td", m("span.cursor-text", { style: "display: inline-block; border: none; max-width: 500px; min-width: 300px; width: auto; white-space: initial;" }, row.siteName)),
                                  country && country === 'MY' && m("td", m("span.cursor-text", row.region)),
                                  country && country === 'MY' && m("td", m("span.cursor-text", row.state)),
                                  country && country === 'MY' && m("td", m("span.cursor-text", row.buChannelType)),
                                  m("td", m("span.cursor-text", row.surveyYMD)),
                                  m("td", m("span.cursor-text", row.uploadBy)),
                                  country && country === 'MY' && m("td", m("span.cursor-text", row.hhpCSE)),
                                  country && country === 'MY' && m("td", m("span.cursor-text", row.hhpAGM)),
                                  country && country === 'MY' && m("td", m("span.cursor-text", row.hhpRGM)),
                                  m("td", m("span.cursor-text", row.modelAlias)),
                                  m("td", m("span.cursor-text", row.modelCode)),
                                  country && country === 'MY' && m("td", m("span.cursor-text", row.marketingName)),
                                  m("td", m("span.cursor-text", row.imei)),
                                  m("td", m("span.cursor-text", row.rrp || 0)),
                                  m("td", m("span.cursor-text", row.consumerName)),
                                  m("td", m("span.cursor-text", row.consumerMobile)),
                                )
                              )
                            )
                          )
                        )
                      ),
                      listReports.length > 0 &&
                      m(".col-12",
                        m(paginationComponent, {
                          currentPage: pagination.pageIndex,
                          pageSize: pagination.pageSize,
                          totalItems: pagination.totalItems,
                          onPageChanged: onPageChanged,
                          onPageSizeChanged: onPageSizeChanged,
                        })
                      )
                    ),
                  )
                )
              )
            ),
          )
        ),

        this.getBaseViews(),
      ]
    }
  }
}
